import React, { useState, useEffect } from 'react';
import { useAuth } from "../../hooks/useSupabaseAuth"

interface PDFDownloadProps {
  contentId: number;
  title: string;
  onClose: () => void;
}

export const PDFDownload: React.FC<PDFDownloadProps> = ({ contentId, title, onClose }) => {
  const [downloading, setDownloading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  useEffect(() => {
    // Check authentication before attempting download
    if (authLoading) {
      return; // Wait for auth check to complete
    }
    
    if (!isAuthenticated) {
      setError('You must be signed in to download content. Please sign in and try again.');
      return;
    }
    
    // Auto-download when component mounts and user is authenticated
    handleDownload();
  }, [contentId, isAuthenticated, authLoading]);

  const handleDownload = async () => {
    // Additional auth check before download
    if (!isAuthenticated) {
      setError('You must be signed in to download content. Please sign in and try again.');
      return;
    }

    try {
      setDownloading(true);
      setError(null);
      
      const response = await fetch(`/api/client/view-content/${contentId}`, {
        credentials: 'include' // Include cookies for auth
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      
      // Close modal after successful download
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (err) {
      console.error('Download error:', err);
      setError(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-2xl max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          {authLoading ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4">
                <svg className="animate-spin h-16 w-16 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Verifying access...</h3>
              <p className="text-gray-600">Checking authentication status</p>
            </>
          ) : downloading ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4">
                <svg className="animate-spin h-16 w-16 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Downloading...</h3>
              <p className="text-gray-600">{title}</p>
            </>
          ) : error ? (
            <>
              <div className="w-16 h-16 mx-auto mb-4 text-red-500">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Download Failed</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <div className="flex gap-3">
                <button
                  onClick={handleDownload}
                  className="flex-1 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 text-green-500">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Download Complete!</h3>
              <p className="text-gray-600 mb-4">Your PDF has been saved to your downloads folder.</p>
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};